import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { FeaturedProduct, Product, ProductDetail } from '../api/catalog';

export interface CartItem {
  id: string;
  name: string;
  platform: string;
  coverUrl: string | null;
  imageUrl: string | null;
  ourPriceCents: number;
}

/** Game keys are single-purchase per title; the basket is capped. */
export const MAX_ITEMS = 5;
const STORAGE_KEY = 'pixelkey_cart';

type Addable = Product | ProductDetail | FeaturedProduct;

function load(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>(load());

  const count = computed(() => items.value.length);
  const subtotalCents = computed(() => items.value.reduce((sum, i) => sum + i.ourPriceCents, 0));
  const isFull = computed(() => items.value.length >= MAX_ITEMS);

  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.value));
    } catch {
      /* storage unavailable — keep in memory only */
    }
  }

  function has(id: string) {
    return items.value.some((i) => i.id === id);
  }

  /** Returns false if the item is already in the cart or the cart is full. */
  function add(p: Addable): boolean {
    if (has(p.id) || isFull.value) return false;
    items.value.push({
      id: p.id,
      name: p.name,
      platform: p.platform,
      coverUrl: p.coverUrl ?? null,
      imageUrl: p.imageUrl ?? null,
      ourPriceCents: p.ourPriceCents,
    });
    persist();
    return true;
  }

  function remove(id: string) {
    items.value = items.value.filter((i) => i.id !== id);
    persist();
  }

  function clear() {
    items.value = [];
    persist();
  }

  return { items, count, subtotalCents, isFull, has, add, remove, clear };
});
